// game/client/src/hud/expertise-data.ts
//
// Single source of player-facing expertise copy, shared by the compact arc
// (expertise-arc.ts — badge + next-tier tooltip) and the Help panel (help-panel.ts
// — full 5-tier trees). Distilled from docs/design/05-heroes-progression.md
// "Expertise tree contents". One line per tier; the tier index is 0-based here,
// so TREE_TIERS[key][0] is the Tier-1 perk.

export type TreeKey = "Forestry" | "Mining" | "Cooking" | "Foraging" | "Artisanship";
export type TreeField = "forestry" | "mining" | "cooking" | "foraging" | "artisanship";

/** Max tier per tree (v1 compressed 5-tier model; each tier = 5 KTS skill-levels). */
export const TREE_CAP = 5;

export interface TierPerk {
  /** Short evocative name for the tier's signature perk. */
  name: string;
  /** One-line "what you get" effect copy. */
  effect: string;
}

export interface TreeDef {
  key: TreeKey;
  field: TreeField;
  /** One-line identity summary (context line in tooltip + help intro). */
  summary: string;
  /** Tier 1→5 perks, 0-indexed. */
  tiers: [TierPerk, TierPerk, TierPerk, TierPerk, TierPerk];
}

// Order matches the arc's top-to-bottom stack.
export const TREE_DEFS: TreeDef[] = [
  {
    key: "Forestry",
    field: "forestry",
    summary: "Wood economy + nature combat.",
    tiers: [
      { name: "Woodcutter", effect: "+20% chopping speed. Grants Nature's Grace + Plant Tree." },
      { name: "Sapper", effect: "+40% chopping. Rare Sageberry drops when a tree falls." },
      { name: "Natural Cleansing", effect: "Chopping clears a debuff. Plant Tree +1 charge." },
      { name: "Forestry Heal", effect: "Heal 20% of damage dealt to armored foes & buildings." },
      { name: "Greenmother's Boon", effect: "Plant Tree cooldown halved, +1 charge, saplings lifesteal." },
    ],
  },
  {
    key: "Mining",
    field: "mining",
    summary: "Stone/ore economy + frontline tank.",
    tiers: [
      { name: "Prospector", effect: "+20% mining speed. Grants Mineral Tap. Less stamina drain." },
      { name: "Quarrier", effect: "+40% mining. Mining stamina drain reduced further." },
      { name: "Mining Fortitude", effect: "−10% damage taken. Restores stamina on melee hits." },
      { name: "Expert Excavator", effect: "Guaranteed ore every 8th deposit. Unlocks Adrenaline Rush." },
      { name: "Earthshaker's Edge", effect: "Gold deposits ping the minimap. Gem-gated stamina stack." },
    ],
  },
  {
    key: "Cooking",
    field: "cooking",
    summary: "Food + group support.",
    tiers: [
      { name: "Cook", effect: "+25% cooking speed. Tier-1 recipes." },
      { name: "Sous-Chef", effect: "+50% cooking speed. Tier-2 recipes." },
      { name: "Sated", effect: "Hunger drain stops entirely. Tier-3 recipes." },
      { name: "Satisfied", effect: "+2 HP/sec regen out of combat. Tier-4 recipes." },
      { name: "Happy Chef", effect: "Cooked food buffs allies (+10 Wit). Tier-5 recipes." },
    ],
  },
  {
    key: "Foraging",
    field: "foraging",
    summary: "Rare drops + scouting & night vision.",
    tiers: [
      { name: "Gatherer", effect: "+20% berry/herb yield. Better rare-drop chance." },
      { name: "Harvester", effect: "+40% yield. Fishing speed bonus with a rod." },
      { name: "Forager's Eye", effect: "Rare nodes ping the minimap when scouted." },
      { name: "Acclimated", effect: "Hunger drains 40% slower. Unlocks Sage Mode." },
      { name: "Night Eye", effect: "Full vision at night (others lose sight range)." },
    ],
  },
  {
    key: "Artisanship",
    field: "artisanship",
    summary: "Build + craft tier unlocks — the path to the Wonder.",
    tiers: [
      { name: "Builder", effect: "Tier-1 builds: Campfire, Tent, Forge." },
      { name: "Mason", effect: "Tier-2 builds (Tavern, Storage) + Forge tier-2 crafts." },
      { name: "Wright", effect: "Tier-3 builds + crafts. Hunter's Lodge." },
      { name: "Artificer", effect: "Forge tier-4 crafts. Infused Lumber processing." },
      { name: "Wonder-Maker", effect: "Unlocks the Wonder — your win condition." },
    ],
  },
];
